import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, BadgeCheck, BadgeX, Loader2, Radio, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { adminApi } from "@/lib/api";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

export default function AdminAbonnes() {
    const navigate = useNavigate();
    const [subs, setSubs] = useState([]);
    const [target, setTarget] = useState(15);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ email: "", name: "" });

    useEffect(() => {
        if (!localStorage.getItem("ms_admin_token")) {
            navigate("/admin/login");
            return;
        }
        load();
    }, [navigate]);

    const load = async () => {
        setLoading(true);
        try {
            const r = await adminApi.get("/admin/subscribers");
            setSubs(r.data?.subscribers || []);
            setTarget(r.data?.target || 15);
        } catch (e) {
            if (e?.response?.status === 401) {
                localStorage.removeItem("ms_admin_token");
                navigate("/admin/login");
            } else {
                toast.error("Erreur de chargement");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!/\S+@\S+\.\S+/.test(form.email)) {
            toast.error("Email invalide");
            return;
        }
        setSaving(true);
        try {
            await adminApi.post("/admin/subscribers", { email: form.email, name: form.name });
            toast.success(`${form.email} actif pour 31 jours`);
            setForm({ email: "", name: "" });
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (s) => {
        if (!window.confirm(`Retirer l'abonné ${s.email} ?`)) return;
        try {
            await adminApi.delete(`/admin/subscribers/${s.id}`);
            toast.success("Abonné retiré");
            await load();
        } catch {
            toast.error("Suppression impossible");
        }
    };

    const activeCount = subs.filter((s) => s.active).length;

    return (
        <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-abonnes-page">
            <SEO title="Admin — Abonnés · Studio Mon Séraphin" description="Gestion des abonnés" path="/admin/abonnes" noindex />
            <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl">
                <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Radio className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
                        <div>
                            <p className="font-display text-sm font-medium">Mon Séraphin · Admin</p>
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Abonnés</p>
                        </div>
                    </div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="admin-abonnes-back-btn">
                        <ArrowLeft className="w-4 h-4" /> Réservations
                    </Link>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-10">
                <div className="rounded-2xl border border-blue-400/20 bg-blue-400/[0.04] p-6">
                    <p className="font-display text-lg font-medium">
                        {activeCount} / {target} abonnés actifs
                    </p>
                    <p className="mt-1 text-xs text-zinc-400 font-body">
                        Les heures de studio sont garanties d'abord à {target} abonnés (3h/semaine chacun = 45h). Les créneaux commerciaux
                        (Découverte / Premium) sont automatiquement plafonnés au reliquat hebdomadaire. Un abonné payant en ligne est activé
                        automatiquement pour 31 jours ; vous pouvez aussi ajouter un abonné manuellement ici.
                    </p>
                </div>

                <form onSubmit={handleAdd} className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0a] p-6" data-testid="admin-sub-form">
                    <h1 className="font-display text-lg font-medium">Ajouter / prolonger un abonné</h1>
                    <div className="mt-4 grid sm:grid-cols-[1fr_1fr_auto] gap-3">
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                            placeholder="email@client.fr"
                            className="rounded-md border border-white/10 bg-white/[0.02] px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-blue-400/50 focus:outline-none"
                            data-testid="admin-sub-email-input"
                        />
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            placeholder="Nom (optionnel)"
                            className="rounded-md border border-white/10 bg-white/[0.02] px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-blue-400/50 focus:outline-none"
                            data-testid="admin-sub-name-input"
                        />
                        <Button type="submit" disabled={saving} className="bg-blue-500 text-white hover:bg-blue-400 rounded-md" data-testid="admin-sub-add-btn">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1" /> Activer 31 j</>}
                        </Button>
                    </div>
                </form>

                <div className="mt-8">
                    {loading ? (
                        <p className="text-sm text-zinc-500">Chargement…</p>
                    ) : subs.length === 0 ? (
                        <p className="text-sm text-zinc-500" data-testid="admin-sub-empty">Aucun abonné pour le moment.</p>
                    ) : (
                        <ul className="space-y-3" data-testid="admin-sub-list">
                            {subs.map((s) => (
                                <li key={s.id} className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4" data-testid={`admin-sub-item-${s.id}`}>
                                    <div className="min-w-0">
                                        <p className="font-display text-sm font-medium text-white truncate flex items-center gap-2">
                                            {s.name || s.email}
                                            {s.active ? (
                                                <span className="inline-flex items-center gap-1 rounded-full border border-blue-400/40 bg-blue-400/10 px-2 py-0.5 text-[10px] text-blue-300">
                                                    <BadgeCheck className="w-3 h-3" /> actif
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-full border border-zinc-600/40 bg-zinc-700/20 px-2 py-0.5 text-[10px] text-zinc-400">
                                                    <BadgeX className="w-3 h-3" /> expiré
                                                </span>
                                            )}
                                        </p>
                                        <p className="mt-0.5 text-xs text-zinc-500 font-body truncate">
                                            {s.email} · jusqu'au {s.active_until ? new Date(s.active_until).toLocaleDateString("fr-FR") : "—"} · {s.week_used_min / 60}h / 3h utilisées cette semaine
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => handleDelete(s)}
                                        className="p-2 text-zinc-400 hover:text-red-400 transition-colors shrink-0"
                                        title="Retirer"
                                        data-testid={`admin-sub-delete-${s.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </main>
        </div>
    );
}
